import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';

import { useCreateFormField, useUpdateFormField } from '../../../../services/supportAdmin';
import { useForm } from '../../../../hooks/useForm';
import MondayColumnSelector from '../components/MondayColumnSelector';

const DEFAULTS = {
  fieldKey: '',
  fieldLabel: '',
  fieldType: 'text',
  fieldPlaceholder: '',
  fieldRequired: false,
  mondayColumnId: '',
  mondayColumnType: '',
};

const FIELD_TYPES = [
  { value: 'text', label: 'Text', mondayTypes: ['text', 'long_text'] },
  { value: 'textarea', label: 'Text Area', mondayTypes: ['doc', 'long_text', 'text'] },
  { value: 'email', label: 'Email', mondayTypes: ['email', 'text'] },
  { value: 'number', label: 'Number', mondayTypes: ['numbers', 'text'] },
  { value: 'phone', label: 'Phone', mondayTypes: ['phone', 'text'] },
  { value: 'date', label: 'Date', mondayTypes: ['date', 'text'] },
  { value: 'select', label: 'Dropdown', mondayTypes: ['dropdown', 'status', 'text'] },
];

const FieldEditorModal = ({ show, toggle, formId, mondayBoardId, field, onSuccess }) => {
  const { form, onChange, setValues } = useForm(DEFAULTS);
  const [optionsText, setOptionsText] = useState('');

  useEffect(() => {
    if (field && field.fieldId) {
      setValues({
        ...DEFAULTS,
        ...field,
        mondayColumnId: field.mondayColumnId || '',
        mondayColumnType: field.mondayColumnType || '',
      });
      // Convert existing options array to text (one per line)
      if (field.fieldOptions) {
        try {
          const opts = typeof field.fieldOptions === 'string'
            ? JSON.parse(field.fieldOptions)
            : field.fieldOptions;
          setOptionsText((opts || []).map((o) => o.label).join('\n'));
        } catch {
          setOptionsText('');
        }
      } else {
        setOptionsText('');
      }
    } else {
      setValues(DEFAULTS);
      setOptionsText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field]);

  const createField = useCreateFormField(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      toast.success('Field created successfully.');
      toggle();
    },
    { onError: (err) => toast.error(err.message) },
  );

  const updateField = useUpdateFormField(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      toast.success('Field updated successfully.');
      toggle();
    },
    { onError: (err) => toast.error(err.message) },
  );

  const generateKey = (label) => {
    return label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_');
  };

  const handleLabelChange = (e) => {
    onChange(e);
    // Auto-generate key if creating new field
    if (!field?.fieldId) {
      const newKey = generateKey(e.target.value);
      const currentAutoKey = generateKey(form.fieldLabel);
      if (!form.fieldKey || form.fieldKey === currentAutoKey) {
        setValues({ ...form, fieldLabel: e.target.value, fieldKey: newKey });
      }
    }
  };

  // Parse comma or newline-separated text into options array
  // Uses label as both label and value (Monday needs human-readable text)
  const parseOptionsText = (text) => {
    if (!text.trim()) return [];

    const options = [];
    text.split('\n').forEach((line) => {
      line.split(',').forEach((part) => {
        const label = part.trim();
        if (label) {
          options.push({
            label,
            value: label, // Use label as value for Monday compatibility
          });
        }
      });
    });

    return options;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    const payload = {
      fieldKey: form.fieldKey,
      fieldLabel: form.fieldLabel,
      fieldType: form.fieldType,
      fieldPlaceholder: form.fieldPlaceholder || null,
      fieldRequired: form.fieldRequired,
      fieldOptions: form.fieldType === 'select' ? parseOptionsText(optionsText) : null,
      mondayColumnId: form.mondayColumnId || null,
      mondayColumnType: form.mondayColumnType || null,
    };

    if (field && field.fieldId) {
      updateField.mutate({
        formId,
        fieldId: field.fieldId,
        payload,
      });
    } else {
      createField.mutate({
        formId,
        payload,
      });
    }
  };

  const isEditing = field && field.fieldId;
  const isLoading = createField.isLoading || updateField.isLoading;
  const currentFieldType = FIELD_TYPES.find((t) => t.value === form.fieldType);

  return (
    <Modal show={show} onHide={() => toggle()} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Field' : 'Add Field'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id="field-form" onSubmit={onSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Field Label *</Form.Label>
                <Form.Control
                  name="fieldLabel"
                  onChange={handleLabelChange}
                  value={form.fieldLabel}
                  required
                  placeholder="e.g., Subject"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Field Key *</Form.Label>
                <Form.Control
                  name="fieldKey"
                  onChange={onChange}
                  value={form.fieldKey}
                  required
                  placeholder="e.g., subject"
                  pattern="^[a-z0-9_]+$"
                />
                <Form.Text className="text-muted">
                  Lowercase letters, numbers, and underscores only
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Field Type</Form.Label>
                <Form.Select
                  name="fieldType"
                  onChange={onChange}
                  value={form.fieldType}
                >
                  {FIELD_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Placeholder</Form.Label>
                <Form.Control
                  name="fieldPlaceholder"
                  onChange={onChange}
                  value={form.fieldPlaceholder}
                  placeholder="Placeholder text..."
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="field-required"
              name="fieldRequired"
              label="This field is required"
              checked={form.fieldRequired}
              onChange={(e) => setValues({ ...form, fieldRequired: e.target.checked })}
            />
          </Form.Group>

          {form.fieldType === 'select' && (
            <Form.Group className="mb-3">
              <Form.Label>Dropdown Options</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                placeholder={'Enter options one per line, or comma-separated:\nOption 1\nOption 2\nOption 3'}
              />
              <Form.Text className="text-muted">
                {parseOptionsText(optionsText).length} option(s) will be created
              </Form.Text>
            </Form.Group>
          )}

          <hr />
          <h5>Monday.com Mapping</h5>
          <p className="text-muted small">
            Map this field to a Monday.com column to sync submitted data.
          </p>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Monday Column</Form.Label>
                <MondayColumnSelector
                  boardId={mondayBoardId}
                  value={form.mondayColumnId}
                  onChange={(columnId, columnType) => setValues({ ...form, mondayColumnId: columnId, mondayColumnType: columnType })}
                  filterTypes={currentFieldType?.mondayTypes}
                  placeholder={
                    mondayBoardId
                      ? 'Select column...'
                      : 'Configure board on form first'
                  }
                  disabled={!mondayBoardId}
                  showItemNameOption={form.fieldType === 'text' || form.fieldType === 'select'}
                />
                {!mondayBoardId && (
                  <Form.Text className="text-warning">
                    Set a Monday board on the form to enable column mapping.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={() => toggle()}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="success"
          form="field-form"
          disabled={isLoading}
        >
          <FontAwesomeIcon
            icon={isLoading ? faSpinner : faCheck}
            spin={isLoading}
          />{' '}
          {isEditing ? 'Update Field' : 'Add Field'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FieldEditorModal;
